import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { auth } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';

const SubscriptionContext = createContext(null);

export const SubscriptionProvider = ({ children }) => {
  const [plan, setPlan] = useState('free');
  const [invoiceCount, setInvoiceCount] = useState(0);
  const [invoiceOverage, setInvoiceOverage] = useState(0);
  const [subscriptionStartDate, setSubscriptionStartDate] = useState(null);
  const [nextApplicablePlan, setNextApplicablePlan] = useState(null);
  const [accountStatus, setAccountStatus] = useState('active');
  const [pastDueAt, setPastDueAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [userToken, setUserToken] = useState(null);
  const [hasPaymentMethod, setHasPaymentMethod] = useState(false);
  const [paymentMethodLoading, setPaymentMethodLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        try {
          const token = await currentUser.getIdToken();
          setUserToken(token);
        } catch (error) {
          console.error("Error getting user token:", error);
          setUserToken(null);
        }
      } else {
        setUserToken(null);
      }
    });
    return () => unsubscribe();
  }, []);

  const fetchWithAuth = useCallback(async (url, options = {}) => {
    if (!userToken) {
      return new Response(JSON.stringify({ error: 'No user token' }), { status: 401, headers: { 'Content-Type': 'application/json' } });
    }
    const newOptions = { ...options };
    newOptions.headers = { ...newOptions.headers, Authorization: `Bearer ${userToken}` };
    return fetch(url, newOptions);
  }, [userToken]);

  const fetchSubscriptionAndPaymentData = useCallback(async () => {
    if (!user || !userToken) {
      setPlan('free');
      setInvoiceCount(0);
      setInvoiceOverage(0);
      setSubscriptionStartDate(null);
      setNextApplicablePlan('free');
      setAccountStatus('active');
      setPastDueAt(null);
      setHasPaymentMethod(false);
      setLoading(false);
      setPaymentMethodLoading(false);
      return;
    }

    setLoading(true);
    setPaymentMethodLoading(true);
    try {
      const profileStatusResponse = await fetchWithAuth('/v1/user-profile-status');
      if (!profileStatusResponse.ok) throw new Error('Failed to check user profile status.');
      const profileStatus = await profileStatusResponse.json();

      if (profileStatus.profileComplete) {
        const subscriptionResponse = await fetchWithAuth('/v1/subscription');
        if (!subscriptionResponse.ok) throw new Error('Failed to fetch subscription data');
        const subData = await subscriptionResponse.json();
        
        setPlan(subData.plan || 'free');
        setInvoiceCount(subData.invoiceCount || 0);
        setInvoiceOverage(subData.invoiceOverage || 0);
        setSubscriptionStartDate(subData.subscriptionStartDate);
        setNextApplicablePlan(subData.nextApplicablePlan || subData.plan || 'free');
        setAccountStatus(subData.accountStatus || 'active');
        setPastDueAt(subData.pastDueAt);

        const paymentMethodsResponse = await fetchWithAuth('/v1/payment-methods');
        if (paymentMethodsResponse.ok) {
          const methods = await paymentMethodsResponse.json();
          const newHasPaymentMethod = Array.isArray(methods.paymentMethods) && methods.paymentMethods.length > 0;
          setHasPaymentMethod(newHasPaymentMethod);
        } else if (paymentMethodsResponse.status === 404) {
          setHasPaymentMethod(false);
        } else {
          throw new Error('Failed to fetch payment methods');
        }

      } else {
        setPlan('free');
        setInvoiceCount(0);
        setInvoiceOverage(0);
        setSubscriptionStartDate(null);
        setNextApplicablePlan('free');
        setAccountStatus('active');
        setPastDueAt(null);
        setHasPaymentMethod(false);
      }
    } catch (error) {
      console.error("Error during data fetch:", error);
      setPlan('free');
      setInvoiceCount(0);
      setInvoiceOverage(0);
      setSubscriptionStartDate(null);
      setNextApplicablePlan('free');
      setAccountStatus('active');
      setPastDueAt(null);
      setHasPaymentMethod(false);
    } finally {
      setLoading(false);
      setPaymentMethodLoading(false);
    }
  }, [user, userToken, fetchWithAuth]);

  useEffect(() => {
    fetchSubscriptionAndPaymentData();
  }, [fetchSubscriptionAndPaymentData]);

  const value = {
    plan,
    setPlan,
    invoiceCount,
    setInvoiceCount,
    invoiceOverage,
    setInvoiceOverage,
    subscriptionStartDate,
    setSubscriptionStartDate,
    nextApplicablePlan,
    setNextApplicablePlan,
    accountStatus,
    setAccountStatus,
    pastDueAt,
    setPastDueAt,
    loading,
    hasPaymentMethod,
    setHasPaymentMethod,
    paymentMethodLoading,
    refetchSubscription: fetchSubscriptionAndPaymentData,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};
